import { useEffect, useState } from "react";
import { api, ADMIN_TOKEN_KEY, ApiError } from "./api";

export type AdminUser = {
  id: string;
  email: string;
  fullName: string | null;
  role: "admin" | "staff";
};

type LoginResponse = { token: string; user: AdminUser };
type MeResponse = { user: AdminUser };

/** Step 1 returns a challenge instead of a session — see auth/login-codes.ts. */
export type LoginChallenge = {
  mfaRequired: true;
  challengeId: string;
  /** Masked, e.g. "e****@vinoga.lv" — just enough to say which inbox. */
  email: string;
  /** Non-production only, so staging can sign in without a real mailbox. */
  devCode?: string;
};

/** Password check. Does NOT sign you in — it emails a one-time code. */
export async function login(email: string, password: string): Promise<LoginChallenge> {
  return api<LoginChallenge>("/api/v1/auth/admin/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

/** Step 2: exchange the emailed code for a session. */
export async function verifyLoginCode(
  challengeId: string,
  code: string,
): Promise<AdminUser> {
  const res = await api<LoginResponse>("/api/v1/auth/admin/login/verify", {
    method: "POST",
    body: JSON.stringify({ challengeId, code }),
  });
  localStorage.setItem(ADMIN_TOKEN_KEY, res.token);
  return res.user;
}

export async function resendLoginCode(challengeId: string): Promise<{ devCode?: string }> {
  return api<{ ok: boolean; devCode?: string }>("/api/v1/auth/admin/login/resend", {
    method: "POST",
    body: JSON.stringify({ challengeId }),
  });
}

export function logout() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export async function fetchMe(): Promise<AdminUser> {
  const res = await api<MeResponse>("/api/v1/auth/admin/me");
  return res.user;
}

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: AdminUser };

export function useAuth(): AuthState & {
  signIn: (email: string, password: string) => Promise<LoginChallenge>;
  completeSignIn: (challengeId: string, code: string) => Promise<void>;
  signOut: () => void;
} {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) {
      setState({ status: "anonymous" });
      return;
    }
    let cancelled = false;

    // Validate the stored token on load. Only a genuine auth rejection
    // (401/403) means the token is bad — clear it and go to login. A transient
    // failure (network drop, 5xx, timeout) must NOT wipe the session, or a
    // refresh or one-second blip logs the admin out. So we retry a few times on
    // transient errors, and if they persist we keep the session authenticated
    // (trusting the stored token) rather than logging out; the next real API
    // call will surface a 401 if the token has actually expired.
    const isAuthError = (err: unknown) =>
      err instanceof ApiError && (err.status === 401 || err.status === 403);

    (async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const user = await fetchMe();
          if (!cancelled) setState({ status: "authenticated", user });
          return;
        } catch (err) {
          if (isAuthError(err)) {
            localStorage.removeItem(ADMIN_TOKEN_KEY);
            if (!cancelled) setState({ status: "anonymous" });
            return;
          }
          // transient — back off and retry
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        }
      }
      // Retries exhausted on transient errors: stay signed in with a
      // placeholder identity so a blip never boots the admin out. The real
      // profile repopulates on the next successful request.
      if (!cancelled) {
        setState({
          status: "authenticated",
          user: { id: "", email: "", fullName: null, role: "admin" },
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    ...state,
    /** Step 1 — returns the challenge; the caller then collects the code. */
    async signIn(email, password) {
      return login(email, password);
    },
    /** Step 2 — completes the sign-in. */
    async completeSignIn(challengeId, code) {
      const user = await verifyLoginCode(challengeId, code);
      setState({ status: "authenticated", user });
    },
    signOut() {
      logout();
      setState({ status: "anonymous" });
    },
  };
}
